import { useInkind } from '@gik/api/inkinds/inkind';
import { patchInkind } from '@gik/api/inkinds/inkinds';
import { optimisticUpdate } from '@gik/core/api';
import type {
  InkindPageAPIModel,
  InkindPagePrivacySettings,
  PatchInkindPageRequest,
} from '@gik/core/models/gik/InkindPage';
import { useInkindCan } from '@gik/core/store/permissions';
import { useUserStore } from '@gik/core/store/UserStore';
import { useBemCN } from '@gik/core/utils/bemBlock';
import repeatElement from '@gik/core/utils/repeatElement';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import { Button } from '@gik/ui/Button';
import { CircleBone } from '@gik/ui/SkeletonLoader';
import { FlowersDisabled } from '@gik/ui/SvgIcon/GikIcons/FlowersDisabled';
import { FlowersOk } from '@gik/ui/SvgIcon/GikIcons/FlowersOk';
import { PageSharingDisabled } from '@gik/ui/SvgIcon/GikIcons/PageSharingDisabled';
import { PageSharingOk } from '@gik/ui/SvgIcon/GikIcons/PageSharingOk';
import { PhoneCallsDisabled, PhoneCallsWarning } from '@gik/ui/SvgIcon/GikIcons/PhoneCallsDisabled';
import { PhoneCallsOk } from '@gik/ui/SvgIcon/GikIcons/PhoneCallsOk';
import { TextMessagesDisabled } from '@gik/ui/SvgIcon/GikIcons/TextMessagesDisabled';
import { TextMessagesOk } from '@gik/ui/SvgIcon/GikIcons/TextMessagesOk';
import { VisitorsDisabled } from '@gik/ui/SvgIcon/GikIcons/VisitorsDisabled';
import { VisitorsOk } from '@gik/ui/SvgIcon/GikIcons/VisitorsOk';
import { SvgIcon } from '@gik/ui/SvgIcon/SvgIcon';
import { Switch } from '@gik/ui/Switch';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { inkindPageTourSelectors } from '../InkindPageTour/InkindPageTour';

interface IInkindPagePrivacyBadgeProps {
  canEdit: boolean;
  value: boolean;
  onChange(value: boolean): void;
  icon: SvgrComponent | React.FC;
  message: string;
  loading: boolean;
}

function InkindPagePrivacyBadge({ icon, message, canEdit, value, loading, onChange }: IInkindPagePrivacyBadgeProps) {
  const bem = useBemCN('inkind-page-privacy-badge');

  return (
    <Button
      {...bem()}
      variant="white"
      circle
      block
      size="base"
      allowTooltipOnMobile
      tooltip={
        <>
          <span>{message}</span>
          {canEdit && (
            <Switch
              {...bem('settings')}
              variant="default"
              size="xs"
              checked={value}
              onValueChange={onChange}
              loading={loading}
            />
          )}
        </>
      }
    >
      <SvgIcon Icon={icon} />
    </Button>
  );
}

interface IInkindPagePrivacyBadgesProps {
  inkindRouteId: string;
  onChanged(prop: PrivacyBadgeType, value: boolean): Promise<void>;
}

export enum PrivacyBadgeType {
  PhoneCalls = 'phoneCalls',
  TextMessages = 'textMessages',
  Flowers = 'flowers',
  Visitors = 'visitors',
  PageSharing = 'pageSharing',
}

function InkindPagePrivacyBadgesComp({ inkindRouteId, onChanged }: IInkindPagePrivacyBadgesProps) {
  const { data: inkindPage, mutate: mutateInkindPage } = useInkind(inkindRouteId);
  const userId = useUserStore(userStore => userStore.id);
  const canEdit = useInkindCan('manage', inkindRouteId, inkindPage?.groupId);
  const bem = useBemCN('inkind-page-privacy-badges');
  const { t } = useTranslation();

  const [savingBadge, setSavingBadge] = React.useState<PrivacyBadgeType>();

  const badgeOptions = {
    canEdit,
    inkindRouteId,
  };

  const handleChange = React.useCallback(
    async (prop: PrivacyBadgeType, newValue) => {
      setSavingBadge(prop);

      let privacySettings: Partial<InkindPagePrivacySettings>;
      let newPageSettings: Partial<InkindPageAPIModel>;
      switch (prop) {
        case PrivacyBadgeType.PhoneCalls:
          privacySettings = {
            allowPhoneCalls: newValue,
          };
          newPageSettings = {
            phoneCalls: newValue,
          };
          break;
        case PrivacyBadgeType.TextMessages:
          privacySettings = {
            allowTexts: newValue,
          };
          newPageSettings = {
            phoneCallRequest: {
              ...inkindPage.phoneCallRequest,
              IsTextsOk: newValue,
            },
          };
          break;
        case PrivacyBadgeType.Flowers:
          privacySettings = {
            allowFlowers: newValue,
          };
          newPageSettings = {
            flowers: newValue,
          };
          break;
        case PrivacyBadgeType.Visitors:
          privacySettings = {
            allowVisitors: newValue,
          };
          newPageSettings = {
            visitors: newValue,
          };
          break;
        case PrivacyBadgeType.PageSharing:
          privacySettings = {
            allowPageSharing: newValue,
          };
          newPageSettings = {
            allowPageSharing: newValue,
          };
          break;
      }

      await optimisticUpdate<PatchInkindPageRequest>(
        { ...inkindPage, ...newPageSettings },
        null,
        mutateInkindPage,
        async () => {
          await patchInkind(inkindRouteId, { privacySettings });
        }
      );

      // await onChanged(prop, newValue);
      setSavingBadge(undefined);
    },
    [inkindPage, inkindRouteId, mutateInkindPage]
  );

  return (
    <div id={inkindPageTourSelectors.privacyPreferences} {...bem()}>
      {inkindPage ? (
        <>
          <InkindPagePrivacyBadge
            {...badgeOptions}
            value={inkindPage.phoneCalls}
            onChange={value => handleChange(PrivacyBadgeType.PhoneCalls, value)}
            loading={savingBadge === PrivacyBadgeType.PhoneCalls}
            icon={
              inkindPage.phoneCalls
                ? PhoneCallsOk
                : inkindPage.phoneCallRequest?.name && inkindPage.phoneCallRequest?.phone
                ? PhoneCallsWarning
                : PhoneCallsDisabled
            }
            message={
              inkindPage.phoneCalls
                ? t(translationKeys.PrivacyBadgePhoneCallsOk)
                : inkindPage.phoneCallRequest?.name && inkindPage.phoneCallRequest?.phone
                ? t(translationKeys.PrivacyBadgePrivacyBadgePhoneCallsAlt, {
                    name: inkindPage.phoneCallRequest.name,
                    phone: inkindPage.phoneCallRequest.phone,
                  })
                : t(translationKeys.PrivacyBadgePhoneCallsDisabled)
            }
          />

          <InkindPagePrivacyBadge
            {...badgeOptions}
            value={inkindPage.phoneCallRequest?.IsTextsOk}
            onChange={value => handleChange(PrivacyBadgeType.TextMessages, value)}
            loading={savingBadge === PrivacyBadgeType.TextMessages}
            icon={inkindPage.phoneCallRequest?.IsTextsOk ? TextMessagesOk : TextMessagesDisabled}
            message={t(
              inkindPage.phoneCallRequest?.IsTextsOk
                ? translationKeys.PrivacyBadgeSmsOk
                : translationKeys.PrivacyBadgeSmsDisabled
            )}
          />

          <InkindPagePrivacyBadge
            {...badgeOptions}
            value={inkindPage.flowers}
            onChange={value => handleChange(PrivacyBadgeType.Flowers, value)}
            loading={savingBadge === PrivacyBadgeType.Flowers}
            icon={inkindPage.flowers ? FlowersOk : FlowersDisabled}
            message={t(
              inkindPage.flowers ? translationKeys.PrivacyBadgeFlowersOk : translationKeys.PrivacyBadgeFlowersDisabled
            )}
          />

          <InkindPagePrivacyBadge
            {...badgeOptions}
            value={inkindPage.visitors}
            onChange={value => handleChange(PrivacyBadgeType.Visitors, value)}
            loading={savingBadge === PrivacyBadgeType.Visitors}
            icon={inkindPage.visitors ? VisitorsOk : VisitorsDisabled}
            message={t(
              inkindPage.visitors
                ? translationKeys.PrivacyBadgeVisitorsOk
                : translationKeys.PrivacyBadgeVisitorsDisabled
            )}
          />

          <InkindPagePrivacyBadge
            {...badgeOptions}
            value={inkindPage.allowPageSharing}
            onChange={value => handleChange(PrivacyBadgeType.PageSharing, value)}
            loading={savingBadge === PrivacyBadgeType.PageSharing}
            icon={inkindPage.allowPageSharing ? PageSharingOk : PageSharingDisabled}
            message={t(
              inkindPage.allowPageSharing
                ? translationKeys.PrivacyBadgeShareOk
                : translationKeys.PrivacyBadgeShareDisabled
            )}
          />
        </>
      ) : (
        repeatElement(5, <CircleBone />)
      )}
    </div>
  );
}

export const InkindPagePrivacyBadges = withComponentErrorBoundary(InkindPagePrivacyBadgesComp);
